import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { ContextBar } from "../components/context-bar";
import { LabeledField } from "../components/onboarding";
import {
  NO_DISCORD_CHANGES,
  HUMAN_APPROVAL_REQUIRED,
  LOCKED_POST_NOTE,
  EngagementConfigBuilder,
  TicketList,
  TicketDetail,
  SuggestionList,
  PollBuilder,
  PollList,
  ScheduledAnnouncementList,
  ReminderRowList,
  EventRowList,
  ScheduleEditor,
  StatisticsView,
  defaultSchedule,
  type PollDraft,
} from "../components/engagement";
import { useSession } from "../hooks/useSession";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  buildEngagementContent,
  type AnnouncementRow,
  type EngagementConfigResponse,
  type EngagementModules,
  type EngagementSchedule,
  type EngagementStatistics,
  type EventRow,
  type PollWithTally,
  type ReminderRow,
  type SuggestionStatus,
  type SuggestionWithTally,
  type TicketRow,
} from "../lib/api";

const RELEASE_REVIEW_HREF = "/app/release-review";

type Tab = "config" | "tickets" | "engage" | "scheduled" | "statistics";

const TABS: { id: Tab; label: string }[] = [
  { id: "config", label: "Config & setup" },
  { id: "tickets", label: "Tickets" },
  { id: "engage", label: "Suggestions & polls" },
  { id: "scheduled", label: "Scheduled content" },
  { id: "statistics", label: "Statistics" },
];

function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

/**
 * Phase 6C engagement admin surface. Server-driven and honest: every value comes
 * from the control API, config drafts and validation never touch Discord, ticket
 * creation and message posting run through the LOCKED engagement pipeline, and
 * every count/tally shown is a real database value.
 */
export function Engagement(): JSX.Element {
  const { state } = useSession();
  const ctx = useAppContext();
  const [tab, setTab] = useState<Tab>("config");

  if (state.kind === "loading") return <LoadingState label="Checking your session…" />;
  if (state.kind === "not-configured")
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to manage engagement.
      </NotConfiguredState>
    );
  if (state.kind === "unavailable") return <ApiUnavailableState />;
  if (state.kind === "anonymous")
    return (
      <>
        <PageHeader title="Engagement" />
        <EmptyState title="Please sign in to continue">
          <Link to="/login">
            <Button variant="primary">Sign in with Discord</Button>
          </Link>
        </EmptyState>
      </>
    );

  const ready = Boolean(ctx.tenantId && ctx.guildId);

  return (
    <>
      <PageHeader title="Engagement: tickets, suggestions, polls & events">
        <p>
          Configure engagement modules, review tickets, run suggestions and polls, schedule content and read real statistics. {NO_DISCORD_CHANGES} {HUMAN_APPROVAL_REQUIRED}
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to manage engagement.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">
              Choose a workspace and enter your ODIN server id above to manage engagement.
            </EmptyState>
          ) : (
            <>
              <div className="row ob-tabs" role="tablist" aria-label="Engagement sections" style={{ gap: "0.5rem", margin: "1rem 0" }}>
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`btn ${tab === t.id ? "btn--primary" : "btn--secondary"}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {tab === "config" ? <ConfigTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "tickets" ? <TicketsTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "engage" ? <EngageTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "scheduled" ? <ScheduledTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
              {tab === "statistics" ? <StatisticsTab tenantId={ctx.tenantId!} guildId={ctx.guildId!} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}

// ── Config & setup ────────────────────────────────────────────────────────────
function ConfigTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [initial, setInitial] = useState<EngagementModules | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .engagement.getConfig(tenantId, guildId)
      .then((r: EngagementConfigResponse) => {
        if (!active) return;
        const active_ = r.configuration?.activeVersionId
          ? r.versions.find((v) => v.id === r.configuration!.activeVersionId)
          : r.versions[r.versions.length - 1];
        if (active_) setInitial(active_.content);
        setLoaded(true);
      })
      .catch((e) => {
        if (active) {
          setError(toMessage(e));
          setLoaded(true);
        }
      });
    return () => {
      active = false;
    };
  }, [tenantId, guildId]);

  const onGenerateDraft = async (modules: EngagementModules) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const version = await api.engagement.createDraftVersion(tenantId, guildId, buildEngagementContent(modules));
      const validation = await api.engagement.validateVersion(tenantId, guildId, version.id);
      setMessage(
        validation.valid
          ? `Draft version ${version.id} (v${version.version}) created and validated. ${NO_DISCORD_CHANGES} ${HUMAN_APPROVAL_REQUIRED}`
          : `Draft version ${version.id} created, but validation found ${validation.errors.length} error(s): ${validation.errors.map((e) => `${e.path}: ${e.message}`).join("; ")}`,
      );
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onValidateRemote = async (modules: EngagementModules) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const version = await api.engagement.createDraftVersion(tenantId, guildId, buildEngagementContent(modules));
      const validation = await api.engagement.validateVersion(tenantId, guildId, version.id);
      setMessage(validation.valid ? `Server validation passed for draft v${version.version}. ${NO_DISCORD_CHANGES}` : `Server validation failed: ${validation.errors.map((e) => e.message).join("; ")}`);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <LoadingState label="Reading engagement configuration…" />;

  return (
    <>
      {error ? <ApiUnavailableState detail={error} /> : null}
      <EngagementConfigBuilder initial={initial} onGenerateDraft={onGenerateDraft} onValidateRemote={onValidateRemote} message={message} busy={busy} releaseReviewHref={RELEASE_REVIEW_HREF} />
    </>
  );
}

// ── Tickets ───────────────────────────────────────────────────────────────────
function TicketsTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [tickets, setTickets] = useState<TicketRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TicketRow | null>(null);
  const [detailMsg, setDetailMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .engagement.listTickets(tenantId, guildId)
      .then((r) => {
        setTickets(r.tickets);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads the same tenant/guild props it depends on
  }, [tenantId, guildId]);

  const select = (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setDetailMsg(null);
    api
      .engagement.getTicket(tenantId, guildId, id)
      .then((r) => setDetail(r.ticket))
      .catch((e) => setDetailMsg(toMessage(e)));
  };

  const act = async (verb: "claim" | "close" | "archive", id: string) => {
    setBusy(true);
    try {
      const fn = verb === "claim" ? api.engagement.claimTicket : verb === "close" ? api.engagement.closeTicket : api.engagement.archiveTicket;
      const r = await fn(tenantId, guildId, id);
      setDetailMsg(`Ticket is now ${r.ticket.status}. Records updated — no live Discord change was made.`);
      load();
      select(id);
    } catch (e) {
      setDetailMsg(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>Locked pipeline.</strong>
        <span>{LOCKED_POST_NOTE}</span>
      </div>

      {error ? (
        <ApiUnavailableState detail={error} />
      ) : tickets === null ? (
        <LoadingState label="Reading tickets…" />
      ) : (
        <TicketList tickets={tickets} selectedId={selectedId} onSelect={select} />
      )}

      {selectedId && detail ? (
        <TicketDetail ticket={detail} busy={busy} message={detailMsg} onClaim={(id) => act("claim", id)} onClose={(id) => act("close", id)} onArchive={(id) => act("archive", id)} />
      ) : selectedId && detailMsg ? (
        <ApiUnavailableState detail={detailMsg} />
      ) : null}
    </div>
  );
}

// ── Suggestions & polls ───────────────────────────────────────────────────────
function EngageTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [suggestions, setSuggestions] = useState<SuggestionWithTally[] | null>(null);
  const [polls, setPolls] = useState<PollWithTally[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollResult, setPollResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([api.engagement.listSuggestions(tenantId, guildId), api.engagement.listPolls(tenantId, guildId)])
      .then(([s, p]) => {
        setSuggestions(s.suggestions);
        setPolls(p.polls);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads the same tenant/guild props it depends on
  }, [tenantId, guildId]);

  const closeSuggestion = async (id: string, status: SuggestionStatus) => {
    setBusy(true);
    try {
      await api.engagement.closeSuggestion(tenantId, guildId, id, status);
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const createPoll = async (draft: PollDraft) => {
    setBusy(true);
    setPollResult(null);
    try {
      const poll = await api.engagement.createPoll(tenantId, guildId, {
        question: draft.question,
        options: draft.options.filter((o) => o.label.trim()),
        allowMultiple: draft.allowMultiple,
        closesAt: draft.closesAt ? new Date(draft.closesAt).toISOString() : null,
      });
      setPollResult(`Poll ${poll.poll.id} created with ${poll.poll.options.length} option(s). ${NO_DISCORD_CHANGES} The poll message is requested through the locked pipeline.`);
      load();
    } catch (e) {
      setPollResult(`Not created: ${toMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const closePoll = async (id: string) => {
    setBusy(true);
    try {
      await api.engagement.closePoll(tenantId, guildId, id);
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      {error ? <ApiUnavailableState detail={error} /> : null}

      <PollBuilder onCreate={createPoll} busy={busy} result={pollResult} />

      {suggestions === null ? <LoadingState label="Reading suggestions…" /> : <SuggestionList suggestions={suggestions} onClose={closeSuggestion} busy={busy} />}
      {polls === null ? <LoadingState label="Reading polls…" /> : <PollList polls={polls} onClose={closePoll} busy={busy} />}
    </div>
  );
}

// ── Scheduled content ─────────────────────────────────────────────────────────
function ScheduledTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [announcements, setAnnouncements] = useState<AnnouncementRow[] | null>(null);
  const [reminders, setReminders] = useState<ReminderRow[] | null>(null);
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([api.engagement.listAnnouncements(tenantId, guildId), api.engagement.listReminders(tenantId, guildId), api.engagement.listEvents(tenantId, guildId)])
      .then(([a, r, e]) => {
        setAnnouncements(a.announcements);
        setReminders(r.reminders);
        setEvents(e.events);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads the same tenant/guild props it depends on
  }, [tenantId, guildId]);

  const toggleAnnouncement = async (row: AnnouncementRow, enabled: boolean) => {
    setBusy(true);
    try {
      await api.engagement.upsertAnnouncement(tenantId, guildId, row.announcementKey, { channelKey: row.channelKey, content: row.content, schedule: row.schedule, enabled });
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const deleteAnnouncement = async (row: AnnouncementRow) => {
    setBusy(true);
    try {
      await api.engagement.deleteAnnouncement(tenantId, guildId, row.announcementKey);
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const deleteReminder = async (row: ReminderRow) => {
    setBusy(true);
    try {
      await api.engagement.deleteReminder(tenantId, guildId, row.id);
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const setEventStatus = async (row: EventRow, status: "CANCELLED" | "COMPLETED") => {
    setBusy(true);
    try {
      await api.engagement.setEventStatus(tenantId, guildId, row.id, status);
      load();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>{NO_DISCORD_CHANGES}</strong>
        <span>{LOCKED_POST_NOTE}</span>
      </div>

      {error ? <ApiUnavailableState detail={error} /> : null}

      <AnnouncementForm tenantId={tenantId} guildId={guildId} onSaved={load} />
      {announcements === null ? <LoadingState label="Reading announcements…" /> : <ScheduledAnnouncementList announcements={announcements} onToggle={toggleAnnouncement} onDelete={deleteAnnouncement} busy={busy} />}

      <ReminderForm tenantId={tenantId} guildId={guildId} onSaved={load} />
      {reminders === null ? <LoadingState label="Reading reminders…" /> : <ReminderRowList reminders={reminders} onDelete={deleteReminder} busy={busy} />}

      <EventForm tenantId={tenantId} guildId={guildId} onSaved={load} />
      {events === null ? <LoadingState label="Reading events…" /> : <EventRowList events={events} onSetStatus={setEventStatus} busy={busy} />}
    </div>
  );
}

function AnnouncementForm({ tenantId, guildId, onSaved }: { tenantId: string; guildId: string; onSaved: () => void }): JSX.Element {
  const [key, setKey] = useState("");
  const [channelKey, setChannelKey] = useState("");
  const [text, setText] = useState("");
  const [schedule, setSchedule] = useState<EngagementSchedule>(defaultSchedule("daily"));
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canSave = key.trim() && channelKey.trim() && text.trim();
  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      await api.engagement.upsertAnnouncement(tenantId, guildId, key.trim(), { channelKey: channelKey.trim(), content: { plainText: text }, schedule, enabled: true });
      setResult(`Announcement "${key.trim()}" saved. ${NO_DISCORD_CHANGES} It posts on schedule through the locked pipeline.`);
      onSaved();
    } catch (e) {
      setResult(`Not saved: ${toMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
      <h4 className="section-title" style={{ marginTop: 0 }}>New / update scheduled announcement</h4>
      <div className="card-grid">
        <LabeledField label="Announcement key">
          <input className="field__input" value={key} onChange={(e) => setKey(e.target.value)} aria-label="Announcement key" />
        </LabeledField>
        <LabeledField label="Channel key">
          <input className="field__input" value={channelKey} onChange={(e) => setChannelKey(e.target.value)} aria-label="Announcement channel key" />
        </LabeledField>
      </div>
      <LabeledField label="Message text">
        <textarea className="field__input" rows={2} value={text} onChange={(e) => setText(e.target.value)} aria-label="Announcement message text" />
      </LabeledField>
      <ScheduleEditor value={schedule} onChange={setSchedule} />
      <button type="button" className="btn btn--primary" disabled={busy || !canSave} onClick={save} style={{ marginTop: "0.5rem" }}>
        {busy ? "Saving…" : "Save announcement"}
      </button>
      {result ? <p className="muted" style={{ marginTop: "0.5rem" }}>{result}</p> : null}
    </div>
  );
}

function ReminderForm({ tenantId, guildId, onSaved }: { tenantId: string; guildId: string; onSaved: () => void }): JSX.Element {
  const [channelKey, setChannelKey] = useState("");
  const [text, setText] = useState("");
  const [schedule, setSchedule] = useState<EngagementSchedule>(defaultSchedule("one-shot"));
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.engagement.createReminder(tenantId, guildId, { channelKey: channelKey.trim() || null, content: text, schedule, enabled: true });
      setResult(`Reminder ${r.reminder.id} saved. ${NO_DISCORD_CHANGES} It fires on schedule through the locked pipeline.`);
      onSaved();
    } catch (e) {
      setResult(`Not saved: ${toMessage(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
      <h4 className="section-title" style={{ marginTop: 0 }}>New reminder</h4>
      <div className="card-grid">
        <LabeledField label="Channel key (optional)" hint="Leave empty to DM the creator.">
          <input className="field__input" value={channelKey} onChange={(e) => setChannelKey(e.target.value)} aria-label="Reminder channel key" />
        </LabeledField>
      </div>
      <LabeledField label="Reminder text">
        <textarea className="field__input" rows={2} value={text} onChange={(e) => setText(e.target.value)} aria-label="Reminder text" />
      </LabeledField>
      <ScheduleEditor value={schedule} onChange={setSchedule} />
      <button type="button" className="btn btn--primary" disabled={busy || !text.trim()} onClick={save} style={{ marginTop: "0.5rem" }}>
        {busy ? "Saving…" : "Save reminder"}
      </button>
      {result ? <p className="muted" style={{ marginTop: "0.5rem" }}>{result}</p> : null}
    </div>
  );
}

function EventForm({ tenantId, guildId, onSaved }: { tenantId: string; guildId: string; onSaved: () => void }): JSX.Element {
  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [announceChannelKey, setAnnounceChannelKey] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const e = await api.engagement.createEvent(tenantId, guildId, {
        title: title.trim(),
        startsAt: new Date(startsAt).toISOString(),
        announceChannelKey: announceChannelKey.trim() || null,
      });
      setResult(`Event ${e.event.id} scheduled. ${NO_DISCORD_CHANGES} Its announcement posts through the locked pipeline.`);
      onSaved();
    } catch (err) {
      setResult(`Not saved: ${toMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
      <h4 className="section-title" style={{ marginTop: 0 }}>New community event</h4>
      <div className="card-grid">
        <LabeledField label="Title">
          <input className="field__input" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Event title" />
        </LabeledField>
        <LabeledField label="Starts at">
          <input className="field__input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} aria-label="Event starts at" />
        </LabeledField>
        <LabeledField label="Announce channel key (optional)">
          <input className="field__input" value={announceChannelKey} onChange={(e) => setAnnounceChannelKey(e.target.value)} aria-label="Event announce channel key" />
        </LabeledField>
      </div>
      <button type="button" className="btn btn--primary" disabled={busy || !title.trim() || !startsAt} onClick={save} style={{ marginTop: "0.5rem" }}>
        {busy ? "Saving…" : "Save event"}
      </button>
      {result ? <p className="muted" style={{ marginTop: "0.5rem" }}>{result}</p> : null}
    </div>
  );
}

// ── Statistics ────────────────────────────────────────────────────────────────
function StatisticsTab({ tenantId, guildId }: { tenantId: string; guildId: string }): JSX.Element {
  const [stats, setStats] = useState<EngagementStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .engagement.getStatistics(tenantId, guildId)
      .then((r) => {
        setStats(r);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)))
      .finally(() => setLoading(false));
  }, [tenantId, guildId]);

  if (loading && !stats) return <LoadingState label="Reading engagement statistics…" />;
  if (error && !stats) return <ApiUnavailableState detail={error} />;
  if (!stats) return <EmptyState title="No statistics available">Engagement has not recorded any activity for this server yet.</EmptyState>;

  return <StatisticsView stats={stats} />;
}
